import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import Avatar from "@/components/Avatar";
import {
  Sparkles, LogOut, LayoutDashboard, Theater, Users, ScrollText, Menu, X, ArrowLeftRight, Library, ToggleRight,
} from "lucide-react";

const NAV = [
  { to: "/platform/overview", label: "Overview", icon: LayoutDashboard },
  { to: "/platform/productions", label: "Productions", icon: Theater },
  { to: "/platform/users", label: "Users", icon: Users },
  { to: "/platform/show-library", label: "Show Library", icon: Library },
  { to: "/platform/modules", label: "Modules", icon: ToggleRight },
  { to: "/platform/legal", label: "Legal Docs", icon: ScrollText },
];

export default function PlatformLayout() {
  const { user, rawUser, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Guard against effective-role impersonation locking a super_admin out of
    // their own /platform screens. Rely on the untouched user object.
    if (rawUser && rawUser.role && rawUser.role !== "super_admin") {
      nav("/productions", { replace: true });
    }
  }, [rawUser, nav]);

  if (!user) return null;

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white">
      <div className="md:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 h-14 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-gold" />
          <span className="font-display text-lg tracking-tight">StageCall</span>
          <span className="text-[10px] uppercase tracking-[0.2em] text-gold ml-1">Platform</span>
        </div>
        <button data-testid="mobile-menu-btn" onClick={() => setOpen((v) => !v)} className="p-2">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <aside
        className={`fixed md:sticky top-0 z-30 h-screen w-72 border-r border-white/10 bg-[#0A0A0A] flex flex-col
          ${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 transition-transform`}
      >
        <div className="px-6 pt-6 pb-4 hidden md:block">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md border border-gold/40 grid place-items-center bg-[#141210]">
              <Sparkles className="w-4 h-4 text-gold" />
            </div>
            <div>
              <div className="font-display text-xl leading-none tracking-tight">StageCall</div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-gold mt-1">Platform Admin</div>
            </div>
          </div>
        </div>

        <div className="mx-4 mb-2 md:mt-0 mt-2 rounded-md border border-gold/30 bg-gradient-to-br from-[#141210] to-[#0F0F0F] p-3">
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Signed in as</div>
          <div className="mt-0.5 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="font-display text-sm truncate" data-testid="platform-user-name">{user.name}</div>
              <div className="text-[10px] text-zinc-500 truncate">{user.email}</div>
            </div>
            <button
              data-testid="platform-exit-btn"
              onClick={() => nav("/productions")}
              title="Exit platform admin"
              className="p-1.5 rounded border border-white/10 text-gold hover:bg-gold/10 shrink-0"
            >
              <ArrowLeftRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-2 md:pt-1 pt-16 space-y-1">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setOpen(false)}
              data-testid={`platform-nav-${to.split("/").pop()}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm mb-0.5 transition-all
                 ${isActive
                    ? "bg-[#121212] text-white border-l-2 border-gold"
                    : "text-zinc-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent"}`
              }
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span className="truncate">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <Avatar user={user} size="w-9 h-9" refresh={user?.avatar_path || 0} />
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate">{user.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gold">{user.role}</div>
            </div>
          </div>
          <button
            data-testid="platform-logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="w-full flex items-center gap-2 justify-center border border-white/15 hover:border-white/30 rounded-md px-3 py-2 text-sm text-zinc-300 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 md:ml-0 mt-14 md:mt-0 min-w-0">
        <div className="max-w-[1400px] mx-auto p-6 md:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
